import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { useBrokerFeature } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import {
  Banner,
  DisplayText,
  Select,
} from "@rentiohq/web-shared/dist/components";
import {
  getMarkerRules,
  IMarkerRule,
  NO_INTEREST_MARKER_RULE,
  REMINDER_BOOKING_MARKER_RULE,
} from "forms/propertyApplicationNotEligible/schema.propertyApplicationNotEligible.utils";
import React from "react";
import { NotificationVariables } from "./components/NotificationVariables/NotificationVariables";
import { VARIABLES_BY_TAB } from "./components/NotificationVariables/NotificationVariables.utils";
import TenantProfileNotificationPreferenceGroup from "./components/TenantProfileNotificationPreferenceGroup";
import { useIsNotificationEnabled } from "./components/TenantProfileNotificationPreferenceGroup/useIsNotificationEnabled";
import VisitReminderSmsTexts from "./components/VisitReminderSmsTexts/VisitReminderSmsTexts";
import * as CONFIG from "./TenantProfileNotificationPreferences.config";
import * as S from "./TenantProfileNotificationPreferences.styles";
import { useSmsNotificationPrice } from "./useSmsNotificationPrice";

/**
 * The tabs whose text carries a link marker, taken from the step-change
 * form's own table rather than restated here, so a rule can never hold in
 * one screen and not the other (DEV-3323). A tab that is absent writes an
 * announcement and has nothing to keep.
 *
 * The reminder is not a step, so it names its rules directly: the booking
 * marker, optional there, and the same no-interest rule object the invitation
 * uses, so each variable has a single spelling (DEV-3330).
 */
const MARKER_RULES_BY_TAB: { [tabKey: string]: IMarkerRule[] | undefined } = {
  invited: getMarkerRules(EIntakeStep.invited),
  candidate_tenant: getMarkerRules(EIntakeStep.candidateTenant),
  booking_reminder: [REMINDER_BOOKING_MARKER_RULE, NO_INTEREST_MARKER_RULE],
};

const MailingPreferences = () => {
  // Only Rentmatch offices get the candidacy block, because only for them does
  // the backend apply a broker's own text to that mail (DEV-3317). Showing it
  // to the others would offer an edit that changes nothing.
  const hasRentMatch = useBrokerFeature(EBrokerFeature.RentMatch);

  // The invitation by email asks for a visit or for a candidacy depending on
  // how the office takes in candidates, the same choice api-next makes when it
  // sends it, so the texts are the ones this office's invitations carry.
  const { broker } = authHooks.useSelf();
  const inviteTexts = CONFIG.getInviteApplicantTexts(broker?.intakeType);

  // Shown next to the SMS switch, so only asked for where that switch is.
  const smsPrice = useSmsNotificationPrice({ shouldFetch: !!hasRentMatch });

  const UNSORTED_TABS = [
    {
      key: "tenant_confirmation_mail",
      // Its own key rather than a new wording on the old one. The translations are
      // shared and any release that refreshes the bundle carries a changed
      // value to production, so renaming the old key would have relabelled the
      // live screen as "visit request" while DEV-3194 is still unreleased and
      // that one tab governs the single mail sent at both moments. The key
      // matches the placeholder key this block already uses.
      label: getLocalizedText(
        "tenant_profile.notification.visit_request_confirmation_mail.tab",
      ),
      enabledKey: CONFIG.PREFERENCE_TENANT_CONFIRMATION_MAIL_ENABLED,
      enabledByDefault: true,
      preferencesConfig: [
        {
          label: getLocalizedText(
            "tenant_profile.notification.tenant_confirmation_mail.label.nl",
          ),
          key: CONFIG.PREFERENCE_TENANT_CONFIRMATION_MAIL_NL,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.visit_request_confirmation_mail.placeholder",
            undefined,
            undefined,
            ELocale.NL,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.tenant_confirmation_mail.label.fr",
          ),
          key: CONFIG.PREFERENCE_TENANT_CONFIRMATION_MAIL_FR,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.visit_request_confirmation_mail.placeholder",
            undefined,
            undefined,
            ELocale.FR,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.tenant_confirmation_mail.label.en",
          ),
          key: CONFIG.PREFERENCE_TENANT_CONFIRMATION_MAIL_EN,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.visit_request_confirmation_mail.placeholder",
            undefined,
            undefined,
            ELocale.EN,
          ),
        },
      ],
    },

    {
      key: "candidacy_confirmation_mail",
      label: getLocalizedText(
        "tenant_profile.notification.candidacy_confirmation_mail.tab",
      ),
      enabledKey: CONFIG.PREFERENCE_CANDIDACY_CONFIRMATION_MAIL_ENABLED,
      // While this broker has never touched the new switch it follows the
      // visit-request one, so turning confirmations off once keeps both mails
      // silent (DEV-3206). The first save here writes its own value and the
      // two become independent.
      fallbackEnabledKey: CONFIG.PREFERENCE_TENANT_CONFIRMATION_MAIL_ENABLED,
      enabledByDefault: true,
      preferencesConfig: [
        {
          label: getLocalizedText(
            "tenant_profile.notification.candidacy_confirmation_mail.label.nl",
          ),
          key: CONFIG.PREFERENCE_CANDIDACY_CONFIRMATION_MAIL_NL,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.candidacy_confirmation_mail.placeholder",
            undefined,
            undefined,
            ELocale.NL,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.candidacy_confirmation_mail.label.fr",
          ),
          key: CONFIG.PREFERENCE_CANDIDACY_CONFIRMATION_MAIL_FR,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.candidacy_confirmation_mail.placeholder",
            undefined,
            undefined,
            ELocale.FR,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.candidacy_confirmation_mail.label.en",
          ),
          key: CONFIG.PREFERENCE_CANDIDACY_CONFIRMATION_MAIL_EN,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.candidacy_confirmation_mail.placeholder",
            undefined,
            undefined,
            ELocale.EN,
          ),
        },
      ],
    },

    {
      key: "visitor",
      label: getLocalizedText("tenant_profile.notification.visitor.tab"),
      enabledKey: CONFIG.PREFERENCE_VISITOR_ENABLED,
      enabledByDefault: true,
      preferencesConfig: [
        {
          label: getLocalizedText(
            "tenant_profile.notification.visitor.label.nl",
          ),
          key: CONFIG.PREFERENCE_VISITOR_NL,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.visitor.placeholder",
            undefined,
            undefined,
            ELocale.NL,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.visitor.label.fr",
          ),
          key: CONFIG.PREFERENCE_VISITOR_FR,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.visitor.placeholder",
            undefined,
            undefined,
            ELocale.FR,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.visitor.label.en",
          ),
          key: CONFIG.PREFERENCE_VISITOR_EN,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.visitor.placeholder",
            undefined,
            undefined,
            ELocale.EN,
          ),
        },
      ],
    },

    {
      key: "not_eligible",
      label: getLocalizedText("tenant_profile.notification.not_eligible.tab"),
      enabledKey: CONFIG.PREFERENCE_NOT_ELIGIBLE_ENABLED,
      enabledByDefault: true,
      preferencesConfig: [
        {
          label: getLocalizedText(
            "tenant_profile.notification.not_eligible.label.nl",
          ),
          key: CONFIG.PREFERENCE_NOT_ELIGIBLE_NL,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.not_eligible.placeholder",
            undefined,
            undefined,
            ELocale.NL,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.not_eligible.label.fr",
          ),
          key: CONFIG.PREFERENCE_NOT_ELIGIBLE_FR,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.not_eligible.placeholder",
            undefined,
            undefined,
            ELocale.FR,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.not_eligible.label.en",
          ),
          key: CONFIG.PREFERENCE_NOT_ELIGIBLE_EN,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.not_eligible.placeholder",
            undefined,
            undefined,
            ELocale.EN,
          ),
        },
      ],
    },
    {
      key: "plan_for_visit",
      label: getLocalizedText("tenant_profile.notification.plan_for_visit.tab"),
      enabledKey: CONFIG.PREFERENCE_PLAN_FOR_VISIT_ENABLED,
      enabledByDefault: true,
      preferencesConfig: [
        {
          label: getLocalizedText(
            "tenant_profile.notification.plan_for_visit.label.nl",
          ),
          key: CONFIG.PREFERENCE_PLAN_FOR_VISIT_NL,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.plan_for_visit.placeholder",
            undefined,
            undefined,
            ELocale.NL,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.plan_for_visit.label.fr",
          ),
          key: CONFIG.PREFERENCE_PLAN_FOR_VISIT_FR,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.plan_for_visit.placeholder",
            undefined,
            undefined,
            ELocale.FR,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.plan_for_visit.label.en",
          ),
          key: CONFIG.PREFERENCE_PLAN_FOR_VISIT_EN,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.plan_for_visit.placeholder",
            undefined,
            undefined,
            ELocale.EN,
          ),
        },
      ],
    },
    {
      key: "invited",
      label: getLocalizedText("tenant_profile.notification.invited.tab"),
      enabledKey: CONFIG.PREFERENCE_INVITED_ENABLED,
      enabledByDefault: true,
      preferencesConfig: [
        {
          label: getLocalizedText(
            "tenant_profile.notification.invited.label.nl",
          ),
          key: CONFIG.PREFERENCE_INVITED_NL,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.invited.placeholder",
            undefined,
            undefined,
            ELocale.NL,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.invited.label.fr",
          ),
          key: CONFIG.PREFERENCE_INVITED_FR,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.invited.placeholder",
            undefined,
            undefined,
            ELocale.FR,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.invited.label.en",
          ),
          key: CONFIG.PREFERENCE_INVITED_EN,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.invited.placeholder",
            undefined,
            undefined,
            ELocale.EN,
          ),
        },
      ],
    },
    {
      // Next to the invitation, because it is what follows one that went
      // unanswered (DEV-3329).
      key: "booking_reminder",
      label: getLocalizedText(
        "tenant_profile.notification.booking_reminder.tab",
      ),
      enabledKey: CONFIG.PREFERENCE_BOOKING_REMINDER_ENABLED,
      enabledByDefault: true,
      preferencesConfig: [
        {
          label: getLocalizedText(
            "tenant_profile.notification.booking_reminder.label.nl",
          ),
          key: CONFIG.PREFERENCE_BOOKING_REMINDER_NL,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.booking_reminder.placeholder",
            undefined,
            undefined,
            ELocale.NL,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.booking_reminder.label.fr",
          ),
          key: CONFIG.PREFERENCE_BOOKING_REMINDER_FR,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.booking_reminder.placeholder",
            undefined,
            undefined,
            ELocale.FR,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.booking_reminder.label.en",
          ),
          key: CONFIG.PREFERENCE_BOOKING_REMINDER_EN,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.booking_reminder.placeholder",
            undefined,
            undefined,
            ELocale.EN,
          ),
        },
      ],
    },
    ...(hasRentMatch
      ? [
          {
            // Sent by hand from a property, so there is nothing to switch
            // off: only the body is the office's own, the subject, heading
            // and button stay the standard ones. Behind the Rentmatch flag
            // because only for a Rentmatch office does api-next read it.
            key: "invite_applicant",
            label: getLocalizedText(
              "tenant_profile.notification.invite_applicant.tab",
            ),
            enabledKey: undefined,
            enabledByDefault: true,
            preferencesConfig: [
              {
                label: getLocalizedText(
                  "tenant_profile.notification.invite_applicant.label.nl",
                ),
                key: inviteTexts.keyNl,
                defaultValue: getLocalizedText(
                  inviteTexts.placeholderKey,
                  undefined,
                  undefined,
                  ELocale.NL,
                ),
              },
              {
                label: getLocalizedText(
                  "tenant_profile.notification.invite_applicant.label.fr",
                ),
                key: inviteTexts.keyFr,
                defaultValue: getLocalizedText(
                  inviteTexts.placeholderKey,
                  undefined,
                  undefined,
                  ELocale.FR,
                ),
              },
              {
                label: getLocalizedText(
                  "tenant_profile.notification.invite_applicant.label.en",
                ),
                key: inviteTexts.keyEn,
                defaultValue: getLocalizedText(
                  inviteTexts.placeholderKey,
                  undefined,
                  undefined,
                  ELocale.EN,
                ),
              },
            ],
          },
          {
            // The switch decides whether the office pays for the SMS; its
            // texts are their own block below it, plain text rather than
            // editor HTML (DEV-3553). Behind the Rentmatch flag because
            // visits are only booked there.
            key: "visit_reminder_sms",
            label: getLocalizedText(
              "tenant_profile.notification.visit_reminder_sms.tab",
            ),
            enabledKey: CONFIG.PREFERENCE_VISIT_REMINDER_SMS_ENABLED,
            enabledByDefault: false,
            preferencesConfig: [],
          },
          {
            key: "candidate_tenant",
            label: getLocalizedText(
              "tenant_profile.notification.candidate_tenant.tab",
            ),
            // No enabledKey, and that is the feature rather than an omission:
            // this mail may be rewritten but never switched off, so there is
            // no switch and nothing stored that a later change could flip.
            enabledKey: undefined,
            enabledByDefault: true,
            preferencesConfig: [
              {
                label: getLocalizedText(
                  "tenant_profile.notification.candidate_tenant.label.nl",
                ),
                key: CONFIG.PREFERENCE_CANDIDATE_TENANT_NL,
                defaultValue: getLocalizedText(
                  "tenant_profile.notification.candidate_tenant.placeholder",
                  undefined,
                  undefined,
                  ELocale.NL,
                ),
              },
              {
                label: getLocalizedText(
                  "tenant_profile.notification.candidate_tenant.label.fr",
                ),
                key: CONFIG.PREFERENCE_CANDIDATE_TENANT_FR,
                defaultValue: getLocalizedText(
                  "tenant_profile.notification.candidate_tenant.placeholder",
                  undefined,
                  undefined,
                  ELocale.FR,
                ),
              },
              {
                label: getLocalizedText(
                  "tenant_profile.notification.candidate_tenant.label.en",
                ),
                key: CONFIG.PREFERENCE_CANDIDATE_TENANT_EN,
                defaultValue: getLocalizedText(
                  "tenant_profile.notification.candidate_tenant.placeholder",
                  undefined,
                  undefined,
                  ELocale.EN,
                ),
              },
            ],
          },
          {
            // Sent by a broker from the candidates page rather than fired by
            // a step change, so there is nothing to switch off: this is only
            // the text the send window starts from, which the broker can
            // still edit or clear for one send (DEV-3339).
            //
            // Behind the same feature flag as the block above, because the
            // window that sends it only exists for a Rentmatch office.
            key: "owner_share_intro",
            label: getLocalizedText(
              "tenant_profile.notification.owner_share_intro.tab",
            ),
            enabledKey: undefined,
            enabledByDefault: true,
            preferencesConfig: [
              {
                label: getLocalizedText(
                  "tenant_profile.notification.owner_share_intro.label.nl",
                ),
                key: CONFIG.PREFERENCE_OWNER_SHARE_INTRO_NL,
                defaultValue: getLocalizedText(
                  "tenant_profile.notification.owner_share_intro.placeholder",
                  undefined,
                  undefined,
                  ELocale.NL,
                ),
              },
              {
                label: getLocalizedText(
                  "tenant_profile.notification.owner_share_intro.label.fr",
                ),
                key: CONFIG.PREFERENCE_OWNER_SHARE_INTRO_FR,
                defaultValue: getLocalizedText(
                  "tenant_profile.notification.owner_share_intro.placeholder",
                  undefined,
                  undefined,
                  ELocale.FR,
                ),
              },
              {
                label: getLocalizedText(
                  "tenant_profile.notification.owner_share_intro.label.en",
                ),
                key: CONFIG.PREFERENCE_OWNER_SHARE_INTRO_EN,
                defaultValue: getLocalizedText(
                  "tenant_profile.notification.owner_share_intro.placeholder",
                  undefined,
                  undefined,
                  ELocale.EN,
                ),
              },
            ],
          },
        ]
      : []),
    {
      key: "waiting_list",
      label: getLocalizedText("tenant_profile.notification.waiting_list.tab"),
      enabledKey: CONFIG.PREFERENCE_WAITING_LIST_ENABLED,
      enabledByDefault: true,
      preferencesConfig: [
        {
          label: getLocalizedText(
            "tenant_profile.notification.waiting_list.label.nl",
          ),
          key: CONFIG.PREFERENCE_WAITING_LIST_NL,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.waiting_list.placeholder",
            undefined,
            undefined,
            ELocale.NL,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.waiting_list.label.fr",
          ),
          key: CONFIG.PREFERENCE_WAITING_LIST_FR,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.waiting_list.placeholder",
            undefined,
            undefined,
            ELocale.FR,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.waiting_list.label.en",
          ),
          key: CONFIG.PREFERENCE_WAITING_LIST_EN,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.waiting_list.placeholder",
            undefined,
            undefined,
            ELocale.EN,
          ),
        },
      ],
    },
    {
      // No switch: the automatic waiting list is turned on per property, and
      // a switch here would be a second answer to that same question. The
      // text starts out as the one the waiting list itself sends, which is
      // what this mail says today (DEV-3335).
      key: "auto_waiting_list",
      label: getLocalizedText(
        "tenant_profile.notification.auto_waiting_list.tab",
      ),
      enabledKey: undefined,
      enabledByDefault: true,
      preferencesConfig: [
        {
          label: getLocalizedText(
            "tenant_profile.notification.auto_waiting_list.label.nl",
          ),
          key: CONFIG.PREFERENCE_AUTO_WAITING_LIST_NL,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.waiting_list.placeholder",
            undefined,
            undefined,
            ELocale.NL,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.auto_waiting_list.label.fr",
          ),
          key: CONFIG.PREFERENCE_AUTO_WAITING_LIST_FR,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.waiting_list.placeholder",
            undefined,
            undefined,
            ELocale.FR,
          ),
        },
        {
          label: getLocalizedText(
            "tenant_profile.notification.auto_waiting_list.label.en",
          ),
          key: CONFIG.PREFERENCE_AUTO_WAITING_LIST_EN,
          defaultValue: getLocalizedText(
            "tenant_profile.notification.waiting_list.placeholder",
            undefined,
            undefined,
            ELocale.EN,
          ),
        },
      ],
    },
  ];

  /**
   * In the order a property's candidates page lays its step tabs out, so a
   * broker reads the same sequence in both places (Jonas, 2026-10-06).
   *
   * Sorted after the list is built rather than written in order, because the
   * list is built conditionally: the candidate-tenant block exists only for a
   * Rentmatch office, and spreading that in at the right spot would put the
   * order back in two places again.
   */
  const TABS = [...UNSORTED_TABS].sort((a, b) => {
    const [stepA, followUpA] = CONFIG.getTabRank(a.key);
    const [stepB, followUpB] = CONFIG.getTabRank(b.key);

    return stepA - stepB || followUpA - followUpB;
  });

  // State
  /**
   * The blocks an office can name, and the second rejection text, which only
   * the not-eligible tab has (DEV-3325).
   *
   * Built here rather than beside the tabs because both carry localized
   * labels, and kept as lookups rather than as fields on every tab, the way
   * the marker rule is, so the tabs without them stay exactly as they were.
   *
   * The second text shares the first text's switch, so the mail is on or off
   * as a whole, and each of its boxes falls back to the matching box of the
   * first. An office that wrote its own rejection before this release opens
   * this block and finds that text rather than the generic default.
   */
  const notEligibleText2 = [
    {
      locale: ELocale.NL,
      label: "nl",
      key: CONFIG.PREFERENCE_NOT_ELIGIBLE_2_NL,
      fallbackKey: CONFIG.PREFERENCE_NOT_ELIGIBLE_NL,
    },
    {
      locale: ELocale.FR,
      label: "fr",
      key: CONFIG.PREFERENCE_NOT_ELIGIBLE_2_FR,
      fallbackKey: CONFIG.PREFERENCE_NOT_ELIGIBLE_FR,
    },
    {
      locale: ELocale.EN,
      label: "en",
      key: CONFIG.PREFERENCE_NOT_ELIGIBLE_2_EN,
      fallbackKey: CONFIG.PREFERENCE_NOT_ELIGIBLE_EN,
    },
  ].map(({ locale, label, key, fallbackKey }) => ({
    label: getLocalizedText(
      `tenant_profile.notification.not_eligible.label.${label}`,
    ),
    key,
    fallbackKey,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.not_eligible.placeholder",
      undefined,
      undefined,
      locale,
    ),
  }));

  /** A line under the switch, for a block that has no text to explain it. */
  const SWITCH_INFO_BY_TAB: { [tabKey: string]: string | undefined } = {
    booking_reminder: getLocalizedText(
      "tenant_profile.notification.booking_reminder.info",
    ),
    // The price comes from the office's own pricing. Without one the line
    // still says the SMS is billed, never a guessed amount.
    visit_reminder_sms: [
      getLocalizedText("tenant_profile.notification.visit_reminder_sms.info"),
      smsPrice === undefined
        ? getLocalizedText(
            "tenant_profile.notification.visit_reminder_sms.billed",
          )
        : getLocalizedText(
            "tenant_profile.notification.visit_reminder_sms.price",
            { price: formatCurrency(smsPrice) },
          ),
    ].join(" "),
    auto_waiting_list: getLocalizedText(
      "tenant_profile.notification.auto_waiting_list.info",
    ),
    owner_share_intro: getLocalizedText(
      "tenant_profile.notification.owner_share_intro.info",
    ),
    invite_applicant: getLocalizedText(
      "tenant_profile.notification.invite_applicant.info",
    ),
  };

  /**
   * Lines under the text boxes.
   *
   * The reminder says where {{bookingUrl}} puts the button, and that it
   * follows the text when the marker is left out. The variables a text may use
   * are in the box beside it (NotificationVariables, DEV-3553).
   *
   * Both tabs that offer the no-interest link say what {{noInterestUrl}} turns
   * into, since it is no longer added on its own (DEV-3330).
   */
  const FIELDS_INFO_BY_TAB: { [tabKey: string]: string[] | undefined } = {
    invited: [getLocalizedText("tenant_profile.notification.invited.markers")],
    booking_reminder: [
      getLocalizedText("tenant_profile.notification.booking_reminder.appended"),
    ],
  };

  const NAME_BY_TAB: {
    [tabKey: string]:
      | { nameKey: string; nameLabel: string; defaultName: string }
      | undefined;
  } = {
    not_eligible: {
      nameKey: CONFIG.PREFERENCE_NOT_ELIGIBLE_NAME,
      nameLabel: getLocalizedText(
        "tenant_profile.notification.not_eligible.name.label",
      ),
      defaultName: getLocalizedText(
        "tenant_profile.notification.not_eligible.default_name.1",
      ),
    },
  };

  const SECOND_GROUP_BY_TAB: {
    [tabKey: string]:
      | {
          nameKey: string;
          nameLabel: string;
          defaultName: string;
          preferencesConfig: typeof notEligibleText2;
        }
      | undefined;
  } = {
    not_eligible: {
      nameKey: CONFIG.PREFERENCE_NOT_ELIGIBLE_2_NAME,
      nameLabel: getLocalizedText(
        "tenant_profile.notification.not_eligible.name.label",
      ),
      defaultName: getLocalizedText(
        "tenant_profile.notification.not_eligible.default_name.2",
      ),
      preferencesConfig: notEligibleText2,
    },
  };

  const [activeTabKey, setActiveTabKey] = React.useState<string | undefined>();

  // Render
  const activeTab = TABS.find(x => x.key === activeTabKey);

  // The variables box shows with the boxes it is about, so only while the
  // tab's mail is on.
  const isActiveTabEnabled = useIsNotificationEnabled({
    enabledKey: activeTab?.enabledKey,
    fallbackEnabledKey: activeTab?.fallbackEnabledKey,
    enabledByDefault: activeTab?.enabledByDefault ?? true,
  });
  return (
    <>
      <Banner
        hasDismiss={false}
        icon="contentPaperEdit"
        title={getLocalizedText("broker.mailing_preferences.info.heading")}
      />

      <Spacer weight={ESpacerWeight.W08} />

      <Select
        id={"select_notification"}
        label={getLocalizedText("tenant_profile.notification.select.title")}
        options={[
          {
            value: undefined,
            label: "-",
          },
          ...TABS.map(x => ({
            value: x.key,
            label: x.label,
          })),
        ]}
        value={activeTabKey}
        onChange={(option: { value: string }) => {
          setActiveTabKey(option.value);
        }}
      />

      {activeTab && (
        <>
          <Spacer weight={ESpacerWeight.W32} />

          <DisplayText size={"small"}>{activeTab.label}</DisplayText>

          {/* <Spacer weight={ESpacerWeight.W02} /> */}

          <S.TabLayout>
            <S.TabContent>
              <TenantProfileNotificationPreferenceGroup
                markerRules={MARKER_RULES_BY_TAB[activeTab.key]}
                enabledKey={activeTab.enabledKey}
                fallbackEnabledKey={activeTab.fallbackEnabledKey}
                enabledByDefault={activeTab.enabledByDefault}
                preferencesConfig={activeTab.preferencesConfig}
                switchInfo={SWITCH_INFO_BY_TAB[activeTab.key]}
                fieldsInfo={FIELDS_INFO_BY_TAB[activeTab.key]}
                {...NAME_BY_TAB[activeTab.key]}
              />

              {activeTab.key === "visit_reminder_sms" && (
                <VisitReminderSmsTexts smsPrice={smsPrice} />
              )}

              {/* Bound to the first block's switch and rendering none of its
                  own, so one switch governs the mail as a whole (DEV-3325). */}
              {SECOND_GROUP_BY_TAB[activeTab.key] && (
                <>
                  <Spacer weight={ESpacerWeight.W32} />

                  <TenantProfileNotificationPreferenceGroup
                    enabledKey={activeTab.enabledKey}
                    showSwitch={false}
                    enabledByDefault={activeTab.enabledByDefault}
                    {...SECOND_GROUP_BY_TAB[activeTab.key]!}
                  />
                </>
              )}
            </S.TabContent>

            {isActiveTabEnabled && VARIABLES_BY_TAB[activeTab.key] && (
              <NotificationVariables
                variables={VARIABLES_BY_TAB[activeTab.key]!}
                footnote={
                  activeTab.key === "visit_reminder_sms"
                    ? getLocalizedText(
                        "tenant_profile.notification.visit_reminder_sms.variables.fallback",
                      )
                    : undefined
                }
              />
            )}
          </S.TabLayout>
        </>
      )}
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default MailingPreferences;
